import { colorToRGBA } from "src/utils/colorToRgba";
import React, { useEffect, useState } from "react";
import { useUser } from "src/atoms/user";
import { useLoaders } from "src/components/InlineLoader";
import { Button, Color, Flex, Icon, Input, Text } from "src/elements";
import { getLoginToken } from "src/utils";
import styled from "styled-components";
import { removeLoginToken, setLoginToken } from "src/utils/localStorage";
import { RoutePostAuthLogin } from "@CommonApi/auth/login";
import { useApi } from "src/api";
import { RouteGetAuthSsoLogin } from "@CommonApi/auth/sso/login";
import { API } from "../../utils/axios";

const Page = styled(Flex)`
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100vw;
  height: 100vh;
  background-image: url(cover.svg);
  background-size: cover;
  background-repeat: no-repeat;
`;

const Wrapper = styled(Flex)`
  flex-direction: column;
  flex-grow: 1;
  gap: 20px;
  width: 100%;
  max-width: 350px;
  margin: 0 auto;
  padding: 100px 0;
  text-align: center;
`;

const LoginCard = styled("form")`
  display: flex;
  flex-direction: column;
  gap: 50px;
  padding: 50px 30px 30px 30px;
  background-color: ${Color.white};
  box-shadow: rgba(63, 142, 106, 0.1) 0px 10px 25px;
  border-radius: 4px;
`;

export const PageLogin = () => {
  const { hasLoader } = useLoaders();
  const { postLoginApi, getVerifyUserApi, getSsoLoginUrl } = useApi();
  const { setUser } = useUser();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoginSuccessful, setIsLoginSuccessful] = useState(false);
  const [isSsoLoginSuccessful, setIsSsoLoginSuccessful] = useState(false);

  useEffect(() => {
    checkIfUserCanBeAutomaticallyLoggedIn();
  }, []);

  const checkIfUserCanBeAutomaticallyLoggedIn = async () => {
    if (!getLoginToken()) return;

    const response = await getVerifyUserApi();

    // Invalid login token.
    if (!response) {
      removeLoginToken();
      return;
    }

    // User verified.
    setUser(response);
  };

  const togglePassword = () => {
    setShowPassword(!showPassword);
  };

  const startLogin = async (event?: React.FormEvent) => {
    event?.preventDefault();
    const response = await postLoginApi({ email, password });

    if (response) {
      setIsLoginSuccessful(true);
      setLoginToken(response.token);
      setUser(response.user);
    }
  };

  const startSsoLogin = async () => {
    const response = await getSsoLoginUrl({
      frontendOrigin: encodeURIComponent(window.location.origin),
      redirectBaseUrl: encodeURIComponent(API),
    });
    if (response?.ssoLoginUrl) {
      setIsSsoLoginSuccessful(true);
      window.location.href = response.ssoLoginUrl;
    }
  };

  // Login token found, show empty page to prevent flashing until user is verified and redirected.
  if (getLoginToken()) {
    return null;
  }

  return (
    <Page>
      <Wrapper>
        <Flex justify="center">
          {import.meta.env.VITE_OMNI_FF_NEW_LOGO === "true" ? (
            <Icon name="logoNew" size={200} />
          ) : (
            <Icon name="logo" size={200} />
          )}
        </Flex>

        <LoginCard onSubmit={startLogin}>
          <Flex direction="column" gap="30px">
            <Input
              title="E-Mail"
              placeholder="example@omnistream.co"
              value={email}
              setValue={setEmail}
              rightIcon={{ name: "email" }}
            />

            <Input
              title="Password"
              placeholder="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              setValue={setPassword}
              rightIcon={{
                name: showPassword ? "eyeOn" : "eyeOff",
                onClick: togglePassword,
              }}
            />
          </Flex>

          <Button
            type="submit"
            width="100%"
            size="big"
            isLoading={hasLoader(RoutePostAuthLogin)}
            isSuccessful={isLoginSuccessful}
            onClick={startLogin}
          >
            Login
          </Button>
        </LoginCard>

        <Button
          type="submit"
          variant="inverted"
          color="greenSmoke"
          isLoading={hasLoader(RouteGetAuthSsoLogin)}
          isSuccessful={isSsoLoginSuccessful}
          onClick={startSsoLogin}
        >
          Login with JumpCloud SSO
        </Button>
      </Wrapper>

      <Flex direction="column" padding="10px">
        <Text variant="small3" color={colorToRGBA(Color.primary, 0.5)}>
          © {new Date().getFullYear()} All Rights Reserved Omnistream.
        </Text>
      </Flex>
    </Page>
  );
};
